import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Search, Sparkles, ChevronRight, Shuffle } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { useAppStore } from '../app/hooks/useAppStore';
import { MODULES, type ModuleId, type ModuleData, getFamiliesForModule, type FamilyDefinition, getAllowedTiersForGroup, getModuleIcon, getModuleColors } from '../content';
import type { FamilyMasteryState } from '../mastery';
import { getFamilyMasteryKey } from '../mastery';
import { DjayIdentity } from '../mascot';
import { getMasteryLabelInfo } from './shared/masteryLabelInfo';
import { MasteryRing } from '../portals/shared/MasteryRing';
import { playSoundIntent } from '../utils/sound/soundIntent';
import { hapticManager } from '../utils/hapticManager';
import { tapSpring } from '../components/shared/motionPresets';
import { MODE_THEME } from './shared/modeTheme';
import { CardSurface } from './shared/CardSurface';
// ─── Types ────────────────────────────────────────────────────
export type FamilyPickerMode = 'sprint' | 'challenge' | 'free';
interface FamilyPickerProps {
  onClose: () => void;
  onSelect: (familyId: string) => void;
  mode: FamilyPickerMode;
}
interface FamilyWithMastery {
  family: FamilyDefinition;
  mastery: FamilyMasteryState | null;
  masteryScore: number;
  attempts: number;
}
// ─── Mode Config ──────────────────────────────────────────────
const MODE_CONFIG = {
  sprint: {
    message: 'Kies een onderwerp om te trainen! 🎯',
    emotion: 'pointing' as const
  },
  free: {
    message: 'Kies wat je wilt oefenen — helemaal vrij! 🎲',
    emotion: 'happy' as const
  },
  challenge: {
    message: 'Kies je uitdaging! Dit wordt spannend 💪',
    emotion: 'excited' as const
  }
} as const;
// ─── Main Component ───────────────────────────────────────────
export function FamilyPicker({
  onClose,
  onSelect,
  mode
}: FamilyPickerProps) {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const familyMastery = state.familyMastery || {};
  // Step 1: null, Step 2: ModuleId
  const [selectedModule, setSelectedModule] = useState<ModuleId | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const config = MODE_CONFIG[mode];
  const theme = MODE_THEME[mode];
  const allowedTiers = useMemo(() => getAllowedTiersForGroup(selectedGroup), [selectedGroup]);
  // Get all families with mastery data, filtered and sorted by mode
  const allFamiliesWithMastery = useMemo(() => {
    const result: FamilyWithMastery[] = [];
    for (const module of MODULES) {
      const families = getFamiliesForModule(module.id);
      for (const family of families) {
        if (mode !== 'free' && !allowedTiers.has(family.tier)) continue;
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup);
        const mastery = familyMastery[masteryKey] || null;
        const masteryScore = mastery?.masteryScore ?? 0;
        const attempts = mastery?.attempts ?? 0;
        if (mode === 'challenge' && masteryScore < 70) continue;
        if (mode === 'sprint' && attempts === 0) continue;
        result.push({
          family,
          mastery,
          masteryScore,
          attempts
        });
      }
    }
    if (mode === 'sprint') result.sort((a, b) => a.masteryScore - b.masteryScore);else if (mode === 'challenge') result.sort((a, b) => b.masteryScore - a.masteryScore);else result.sort((a, b) => a.family.description.localeCompare(b.family.description));
    return result;
  }, [selectedGroup, familyMastery, allowedTiers, mode]);
  // Calculate module aggregates for Step 1
  const moduleAggregates = useMemo(() => {
    return MODULES.map(module => {
      const moduleFamilies = allFamiliesWithMastery.filter(f => f.family.module === module.id);
      const count = moduleFamilies.length;
      const avgMastery = count > 0 ? moduleFamilies.reduce((sum, f) => sum + f.masteryScore, 0) / count : 0;
      return {
        module,
        count,
        avgMastery
      };
    }).filter(m => m.count > 0); // Only show modules that have available families for this mode
  }, [allFamiliesWithMastery]);
  // Get families for selected module (Step 2)
  const selectedModuleFamilies = useMemo(() => {
    if (!selectedModule) return [];
    return allFamiliesWithMastery.filter(f => f.family.module === selectedModule);
  }, [allFamiliesWithMastery, selectedModule]);
  // Filter by search query (Step 2)
  const filteredFamilies = useMemo(() => {
    if (!searchQuery.trim()) return selectedModuleFamilies;
    const query = searchQuery.toLowerCase();
    return selectedModuleFamilies.filter(({
      family
    }) => family.familyId.toLowerCase().includes(query) || family.description.toLowerCase().includes(query) || family.skill.toLowerCase().includes(query) || family.subSkill.toLowerCase().includes(query));
  }, [selectedModuleFamilies, searchQuery]);
  const hasPracticedFamilies = useMemo(() => Object.values(familyMastery).some(m => m.attempts > 0), [familyMastery]);
  const hasChallengeReadyFamilies = useMemo(() => Object.values(familyMastery).some(m => m.masteryScore >= 70), [familyMastery]);
  const djaysPick = allFamiliesWithMastery.length > 0 ? allFamiliesWithMastery[0] : null;
  const showDjayPickInModule = !searchQuery && selectedModule && filteredFamilies.length > 0 && djaysPick && filteredFamilies[0].family.familyId === djaysPick.family.familyId;
  return <div className="bg-slate-50 min-h-screen pb-8" data-id="element-2811" data-mp="23scncgjb6io6">
      {/* ── Colorful Header ── */}
      <div className={`bg-gradient-to-br ${theme.gradient} relative overflow-hidden`} data-id="element-2812" data-mp="23q07c8x42idr">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-2813" data-mp="1puxv741o7wsi" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-2xl translate-y-1/4 -translate-x-1/4" data-id="element-2814" data-mp="by92xergu3o" />

        <div className="relative z-10 px-6 sm:px-8 md:px-10 pt-6 md:pt-10 pb-6 max-w-4xl mx-auto" data-id="element-2815" data-mp="3rpc4prm21ps5">
          <button onClick={selectedModule ? () => {
          setSelectedModule(null);
          setSearchQuery('');
        } : onClose} className="flex items-center gap-2 text-white/80 hover:text-white font-bold mb-4 transition-colors text-sm" data-id="element-2816" data-mp="3mrsqgrpcxd07">
            <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-2817" data-mp="9bl7dac71jsi">
              <ArrowLeft className="w-4 h-4" data-id="element-2818" data-mp="59hmjv3mx2uk" />
            </div>
            {selectedModule ? 'Terug naar vakken' : 'Terug naar Oefenlab'}
          </button>

          <div className="flex items-center gap-4" data-id="element-2819" data-mp="2q0rt7pz9pthf">
            <motion.div className="flex-shrink-0" animate={{
            y: [0, -4, 0]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-2820" data-mp="2pzyxdk1ukoe2">
              <DjayIdentity size="lg" emotion={config.emotion} animate data-id="element-2821" data-mp="1eim83gx2tph8" />
            </motion.div>

            <div className="flex-1 min-w-0" data-id="element-2822" data-mp="1jaw3hcgynhqw">
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight flex items-center gap-3" data-id="element-2823" data-mp="1o97f9sqtg2br">
                {theme.title}
                {mode === 'free' && <span className="text-[10px] font-black bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg uppercase tracking-wider" data-id="element-2824" data-mp="1x9vtook4g4p6">
                    Experimenteel
                  </span>}
              </h1>
              <div className="mt-2 bg-white/20 backdrop-blur-sm rounded-2xl px-3 py-2 inline-block max-w-full" data-id="element-2825" data-mp="2wnukkp456xq">
                <p className="text-white text-sm font-bold truncate" data-id="element-2826" data-mp="7w0eoxolnzwm">
                  {selectedModule ? `Kies een onderwerp voor ${MODULES.find(m => m.id === selectedModule)?.title}` : config.message}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-10 pt-6" data-id="element-2827" data-mp="2jkf4mj9pukka">
        <AnimatePresence mode="wait" data-id="element-2828" data-mp="2ejrctvdnxzp0">
          {!selectedModule /* ── Step 1: Module Selection ── */ ? <motion.div key="step1" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="space-y-6" data-id="element-2829" data-mp="1qtx15h52yh6c">
              {allFamiliesWithMastery.length === 0 ? <EmptyState mode={mode} hasPracticedFamilies={hasPracticedFamilies} hasChallengeReadyFamilies={hasChallengeReadyFamilies} data-id="element-2830" data-mp="377vjok7z0ita" /> : <>
                  {/* Djay Kiest Quick Start (F-2) */}
                  {djaysPick && mode !== 'free' && <DjayPickCard item={djaysPick} onSelect={onSelect} accentColor={theme.primaryHue} isQuickStart={true} data-id="element-2831" data-mp="k10ms26ijgda" />}

                  <div data-id="element-2832" data-mp="1gq86d4nknfek">
                    <h2 className="text-lg font-black text-slate-800 mb-4" data-id="element-2833" data-mp="1gonc7g565yh8">
                      Kies een vak
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-2834" data-mp="12w7f0vyy8uo8">
                      {moduleAggregates.map(({
                  module,
                  count,
                  avgMastery
                }, index) => <ModuleCard key={module.id} module={module} count={count} avgMastery={avgMastery} index={index} onClick={() => setSelectedModule(module.id)} data-id="element-2835" data-mp="1vl4ktzlz4z03" data-mpi={index} />)}
                    </div>
                  </div>
                </>}
            </motion.div> /* ── Step 2: Family Selection ── */ : <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} className="space-y-4" data-id="element-2836" data-mp="1kn0sffvb7w84">
              <div className="sticky top-0 z-30 bg-slate-50/95 backdrop-blur-sm py-2 -mx-6 px-6 sm:mx-0 sm:px-0" data-id="element-2837" data-mp="11qsihyrcqx6h">
                <Input type="text" placeholder="Zoek een onderwerp..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-2839" data-mp="i5s9jnapwbie" />} variant="student" size="sm" data-id="element-2838" data-mp="16nizqpew7yth" />
              </div>

              <div className="flex items-center justify-between mb-2" data-id="element-2840" data-mp="s1lt3qmut59o">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-2841" data-mp="161wnv4rab404">
                  {filteredFamilies.length} onderwerp
                  {filteredFamilies.length !== 1 ? 'en' : ''}
                </p>
                {/* F-FLOW-5 + F-FLOW-10: "Verras me" shortcut — only for sprint/free,
                 keuze is betekenisvol bij challenge dus daar niet. */}
                {mode !== 'challenge' && filteredFamilies.length > 1 && <motion.button {...tapSpring} onClick={() => {
              const pick = filteredFamilies[Math.floor(Math.random() * filteredFamilies.length)];
              if (pick) {
                playSoundIntent('reward.streak');
                hapticManager.hapticLight();
                onSelect(pick.family.familyId);
              }
            }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-black uppercase tracking-wider shadow-sm hover:shadow-md transition-shadow" data-id="element-2842" data-mp="1osxjpjx3yjfg">
                    <Shuffle className="w-3.5 h-3.5" data-id="element-2843" data-mp="1a5voqza7fo6s" />
                    Verras me
                  </motion.button>}
              </div>

              {filteredFamilies.length === 0 ? <div className="text-center py-12" data-id="element-2844" data-mp="3bx8pdc5539x1">
                  <p className="text-slate-500 font-medium" data-id="element-2845" data-mp="3fx94z03z5r5o">
                    Geen onderwerpen gevonden voor deze zoekopdracht.
                  </p>
                </div> : <div className="space-y-2" data-id="element-2846" data-mp="33n9sk0aim1p3">
                  {showDjayPickInModule && <DjayPickCard item={filteredFamilies[0]} onSelect={onSelect} accentColor={theme.primaryHue} isQuickStart={false} data-id="element-2847" data-mp="2yo6ks835hhdf" />}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-2848" data-mp="3rcc6oy5xxceu">
                    {filteredFamilies.slice(showDjayPickInModule ? 1 : 0).map(({
                family,
                masteryScore,
                attempts
              }, index) => <FamilyCard key={family.familyId} family={family} masteryScore={masteryScore} attempts={attempts} index={index} onSelect={onSelect} data-id="element-2849" data-mp="3ddw0d8bzpnx8" data-mpi={index} />)}
                  </div>
                </div>}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
// ─── Sub-components ───────────────────────────────────────────
function ModuleCard({
  module,
  count,
  avgMastery,
  index,
  onClick
}: {
  module: ModuleData;
  count: number;
  avgMastery: number;
  index: number;
  onClick: () => void;
}) {
  const ModIcon = getModuleIcon(module.icon);
  const colors = getModuleColors(module.color);
  const masteryInfo = getMasteryLabelInfo(avgMastery);
  return <CardSurface initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: Math.min(index * 0.05, 0.3)
  }} onClick={onClick} className="w-full p-4 rounded-3xl border-2 border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg transition-all group overflow-hidden flex flex-col h-full" data-id="element-2850" data-mp="3dzkvx2fzyw3">
      <header className="flex items-start justify-between mb-3 w-full" data-id="element-2851" data-mp="2km174daaokf7">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${colors.light}`} data-id="element-2852" data-mp="2gmio5n21ajf0">
          <ModIcon className={`w-6 h-6 ${colors.text}`} data-id="element-2853" data-mp="2uk7a6y7u7wln" />
        </div>
        <div className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-1 rounded-lg" data-id="element-2854" data-mp="2plxf0nm44w01">
          {count} items
        </div>
      </header>

      <h3 className="text-base font-black text-slate-800 mb-1 group-hover:text-slate-900 transition-colors w-full" data-id="element-2855" data-mp="21x4cg8azjpwg">
        {module.title}
      </h3>

      <div className="mt-auto pt-3 w-full" data-id="element-2856" data-mp="2bn657dea9whp">
        <div className="flex items-center justify-between mb-1.5" data-id="element-2857" data-mp="2bnz09h3vqxk0">
          <span className={`text-xs font-bold ${masteryInfo.color}`} data-id="element-2858" data-mp="26uwhm40n58il">
            {masteryInfo.label}
          </span>
          <span className="text-[10px] font-bold text-slate-400" data-id="element-2859" data-mp="1j4sdlvjjj6r2">
            {Math.round(avgMastery)}%
          </span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-2860" data-mp="1e5fpz8kuhvnf">
          <motion.div className="h-full rounded-full" style={{
          backgroundColor: masteryInfo.ring
        }} initial={{
          width: 0
        }} animate={{
          width: `${Math.min(100, Math.max(0, avgMastery))}%`
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-2861" data-mp="3e99ni1vogguq" />
        </div>
      </div>
    </CardSurface>;
}
function EmptyState({
  mode,
  hasPracticedFamilies,
  hasChallengeReadyFamilies
}: {
  mode: FamilyPickerMode;
  hasPracticedFamilies: boolean;
  hasChallengeReadyFamilies: boolean;
}) {
  const title = mode === 'sprint' && !hasPracticedFamilies ? 'Je moet eerst meer oefenen!' : mode === 'challenge' && !hasChallengeReadyFamilies ? 'Nog geen onderwerpen klaar' : 'Geen resultaten gevonden';
  const message = mode === 'sprint' && !hasPracticedFamilies ? 'Maak eerst wat oefeningen in een vak. Daarna kun je hier extra trainen!' : mode === 'challenge' && !hasChallengeReadyFamilies ? 'Oefen meer tot je minimaal 70% beheersing hebt.' : 'Probeer een andere zoekopdracht of selecteer een ander vak.';
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="flex flex-col items-center justify-center py-16 text-center px-6" data-id="element-2862" data-mp="kx9edegn6hqe">
      <div className="mb-4" data-id="element-2863" data-mp="awz524xq9ugi">
        <DjayIdentity size="xl" emotion="thinking" data-id="element-2864" data-mp="b0wo6qkb2m0e" />
      </div>
      <h3 className="text-xl font-black text-slate-800 mb-2" data-id="element-2865" data-mp="5zz7yjiuw21j">{title}</h3>
      <p className="text-sm text-slate-500 max-w-sm font-medium leading-relaxed" data-id="element-2866" data-mp="1ynhycw2lest">
        {message}
      </p>
    </motion.div>;
}
function FamilyCard({
  family,
  masteryScore,
  attempts,
  index,
  onSelect
}: {
  family: FamilyDefinition;
  masteryScore: number;
  attempts: number;
  index: number;
  onSelect: (id: string) => void;
}) {
  const masteryInfo = getMasteryLabelInfo(masteryScore);
  return <CardSurface initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: Math.min(index * 0.03, 0.3)
  }} onClick={() => onSelect(family.familyId)} className="w-full p-3.5 rounded-2xl border-2 border-slate-200 bg-white hover:border-indigo-300 hover:shadow-lg transition-all group overflow-hidden flex flex-col h-full" data-id="element-2867" data-mp="2jl6tfg5f3n1u">
      <header className="flex items-start justify-between mb-2 w-full" data-id="element-2868" data-mp="20tntji6owp41">
        <div className="flex-1 min-w-0 pr-2" data-id="element-2869" data-mp="1mxjzbovtggrk">
          <h4 className="text-sm font-black text-slate-800 leading-tight line-clamp-2 group-hover:text-indigo-700 transition-colors" data-id="element-2870" data-mp="1rusk7x8cmywi">
            {family.description}
          </h4>
          <div className="flex items-center gap-1.5 mt-1.5" data-id="element-2871" data-mp="3h85t7zrepb3y">
            <Badge variant="neutral" size="sm" data-id="element-2872" data-mp="3m7s8eeqsp1fe">
              {family.levelCode}
            </Badge>
            <span className="text-[10px] text-slate-400 font-medium truncate" data-id="element-2873" data-mp="8fbjdtv4og6">
              {family.skill}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0" data-id="element-2874" data-mp="8zph63i5sb18">
          <MasteryRing score={masteryScore} size={40} data-id="element-2875" data-mp="90r3530pqhxq" />
        </div>
      </header>

      <div className="mt-auto pt-2 w-full" data-id="element-2876" data-mp="486d8ckxgozh">
        <div className="flex items-center justify-between mb-1.5" data-id="element-2877" data-mp="474pp8mq6a0v">
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${masteryInfo.bg} ${masteryInfo.color}`} data-id="element-2878" data-mp="iwu26009q0gg">
            {masteryInfo.label}
          </span>
          {attempts > 0 && <span className="text-[10px] font-bold text-slate-400" data-id="element-2879" data-mp="ry0ncmdv3zg1">
              {attempts}×
            </span>}
        </div>
        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-2880" data-mp="2tpwy3ts7h59r">
          <motion.div className="h-full rounded-full" style={{
          backgroundColor: masteryInfo.ring
        }} initial={{
          width: 0
        }} animate={{
          width: `${Math.min(100, Math.max(0, masteryScore))}%`
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-2881" data-mp="2trzwb13ii4pk" />
        </div>
      </div>
    </CardSurface>;
}
function DjayPickCard({
  item,
  onSelect,
  accentColor,
  isQuickStart
}: {
  item: FamilyWithMastery;
  onSelect: (familyId: string) => void;
  accentColor: string;
  isQuickStart: boolean;
}) {
  const {
    family,
    masteryScore,
    attempts
  } = item;
  const module = MODULES.find(m => m.id === family.module);
  const ModIcon = getModuleIcon(module?.icon || '');
  const colors = module ? getModuleColors(module.color) : null;
  const masteryInfo = getMasteryLabelInfo(masteryScore);
  return <CardSurface initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} disableDefaultSound onClick={() => {
    playSoundIntent('reward.streak');
    onSelect(family.familyId);
  }} className="w-full mb-4 p-4 rounded-3xl border-3 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 hover:shadow-xl transition-all group overflow-hidden before:absolute before:top-0 before:right-0 before:w-32 before:h-32 before:bg-amber-200/30 before:rounded-full before:blur-2xl before:-translate-y-1/2 before:translate-x-1/4 before:pointer-events-none" data-id="element-2882" data-mp="2yi12jpny5ccf">
      <header className="flex items-center gap-1.5 mb-3 w-full relative z-10" data-id="element-2883" data-mp="3prdp1799p952">
        <div className="bg-amber-200/60 text-amber-600 p-1 rounded-lg" data-id="element-2884" data-mp="3b2hjbguoo87m">
          <Sparkles className="w-3.5 h-3.5" data-id="element-2885" data-mp="3b053b98hk7x7" />
        </div>
        <span className="text-[11px] font-black text-amber-600 uppercase tracking-wider" data-id="element-2886" data-mp="3g09bmbz8pwcc">
          {isQuickStart ? "Djay's keuze — Snel starten!" : "Djay's keuze voor dit vak"}
        </span>
      </header>

      <div className="flex items-center gap-3 relative z-10 w-full" data-id="element-2887" data-mp="cdisyhgpygha">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-white shadow-sm ${colors?.text || 'text-slate-500'}`} data-id="element-2888" data-mp="m7502ehxjq92">
          <ModIcon className="w-6 h-6" data-id="element-2889" data-mp="d707haheawey" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-2890" data-mp="d5ouck228cb6">
          <h4 className="text-base font-black text-slate-900 leading-tight line-clamp-2" data-id="element-2891" data-mp="88ouiqvt05si">
            {family.description}
          </h4>
          <div className="flex items-center gap-2 mt-1" data-id="element-2892" data-mp="3a3tip8q9i30">
            <Badge variant="warning" size="sm" data-id="element-2893" data-mp="3uo9mozt6ilke">
              {family.levelCode}
            </Badge>
            <span className="text-xs text-slate-500 font-medium truncate" data-id="element-2894" data-mp="3pon7wlxpcx9h">
              {module?.title}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2" data-id="element-2895" data-mp="3lp64rz5r0c7h">
          <MasteryRing score={masteryScore} size={44} data-id="element-2896" data-mp="3goi5kq7d8p26" />
          <ChevronRight className="w-5 h-5 text-amber-400 group-hover:translate-x-1 transition-transform" data-id="element-2897" data-mp="3gq2w84qk7nur" />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 relative z-10 w-full" data-id="element-2898" data-mp="1r7mm6y6mcfc1">
        <div className="flex-1" data-id="element-2899" data-mp="1w67i4leu8cuo">
          <div className="flex items-center justify-between mb-1.5" data-id="element-2900" data-mp="1w54ucmctaqiz">
            <span className={`text-xs font-bold ${masteryInfo.color}`} data-id="element-2901" data-mp="215k5d79mflxs">
              {masteryInfo.label}
            </span>
            <span className="text-[10px] font-bold text-slate-400" data-id="element-2902" data-mp="2575rlj81pckn">
              {attempts}× geoefend
            </span>
          </div>
          <div className="w-full h-2 bg-white/80 rounded-full overflow-hidden" data-id="element-2903" data-mp="18ig6lk14g3ug">
            <motion.div className="h-full rounded-full bg-amber-400" initial={{
            width: 0
          }} animate={{
            width: `${Math.min(100, Math.max(0, masteryScore))}%`
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} data-id="element-2904" data-mp="1dghs0bm0sf93" />
          </div>
        </div>
      </div>
    </CardSurface>;
}